import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2 } from 'lucide-react';

interface UnsplashImage {
  id: string;
  urls: {
    regular: string;
    small: string;
    thumb: string;
  };
  alt_description: string;
  user: {
    name: string;
  };
}

interface UnsplashImageSelectorProps {
  onSelectImage: (imageUrl: string, alt: string) => void;
  onClose: () => void;
}

export const UnsplashImageSelector: React.FC<UnsplashImageSelectorProps> = ({
  onSelectImage,
  onClose
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [images, setImages] = useState<UnsplashImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // This is a mock function - in a real implementation, you would use the Unsplash API
  // You would need to register for an Unsplash API key and use it here
  const searchUnsplash = async (query: string) => {
    if (!query.trim()) {
      setImages([]);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // This is a placeholder - in a real implementation, you would call the Unsplash API
      // Example: const response = await fetch(`https://api.unsplash.com/search/photos?query=${query}&client_id=YOUR_API_KEY`);
      
      // For now, we'll use a mock response
      setTimeout(() => {
        const mockImages: UnsplashImage[] = [
          {
            id: '1',
            urls: {
              regular: 'https://images.unsplash.com/photo-1682687982107-14492010e05e',
              small: 'https://images.unsplash.com/photo-1682687982107-14492010e05e?w=400',
              thumb: 'https://images.unsplash.com/photo-1682687982107-14492010e05e?w=200'
            },
            alt_description: 'Sample image 1',
            user: { name: 'Unsplash User 1' }
          },
          {
            id: '2',
            urls: {
              regular: 'https://images.unsplash.com/photo-1682695796954-bad0d0f59ff1',
              small: 'https://images.unsplash.com/photo-1682695796954-bad0d0f59ff1?w=400',
              thumb: 'https://images.unsplash.com/photo-1682695796954-bad0d0f59ff1?w=200'
            },
            alt_description: 'Sample image 2',
            user: { name: 'Unsplash User 2' }
          }
        ];
        
        setImages(mockImages);
        setLoading(false);
      }, 1000);
    } catch (err) {
      setError('Failed to fetch images from Unsplash');
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    searchUnsplash(searchQuery);
  };

  const handleSelectImage = (image: UnsplashImage) => {
    onSelectImage(image.urls.regular, image.alt_description || 'Unsplash image');
  };

  // Display a message about Unsplash API requirements
  useEffect(() => {
    if (images.length === 0 && !loading && !error) {
      setError('Note: To use Unsplash images in a production environment, you need to register for an Unsplash API key and implement proper attribution.');
    }
  }, [images, loading, error]);

  return (
    <div className="flex flex-col gap-4">
      <form onSubmit={handleSearch} className="flex gap-2">
        <Input
          type="text"
          placeholder="Search Unsplash..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1"
        />
        <Button type="submit" disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
        </Button>
      </form>

      {error && <p className="text-sm text-red-500">{error}</p>}

      <div className="grid grid-cols-2 gap-2 max-h-[400px] overflow-y-auto">
        {images.map((image) => (
          <div
            key={image.id}
            className="relative group cursor-pointer"
            onClick={() => handleSelectImage(image)}
          >
            <img
              src={image.urls.small}
              alt={image.alt_description || 'Unsplash image'}
              className="w-full h-32 object-cover rounded-md"
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-end p-2">
              <span className="text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                Photo by {image.user.name} on Unsplash
              </span>
            </div>
          </div>
        ))}
      </div>

      {loading && (
        <div className="flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      )}

      <div className="flex justify-between mt-4">
        <p className="text-xs text-gray-500">
          To use Unsplash images, you need to register for an API key and implement proper attribution.
        </p>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
};
